'use client';

import Image from 'next/image';
import { useId, useRef, useState, type KeyboardEvent } from 'react';
import './about-kitchen-scene.css';

const STEPS = [
  {
    label: 'Собираем',
    photo: '/ingredients.webp',
    alt: 'Сушист выкладывает лосось на рис и нори для будущего ролла',
    description: 'Выкладываем начинку на рис и нори.',
  },
  {
    label: 'Нарезаем',
    photo: '/fresh.webp',
    alt: 'Сушист нарезает готовый ролл на кухонной доске',
    description: 'Скручиваем, нарезаем и собираем роллы к отправке.',
  },
];

export function AboutKitchenScene() {
  const id = useId();
  const [active, setActive] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleTabKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') next = 1 - index;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = STEPS.length - 1;
    else return;
    event.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section className="ab-kitchen" aria-labelledby={`${id}-title`}>
      <div className="ab-kitchen-heading">
        <div>
          <span className="ab-kitchen-eyebrow">На кухне</span>
          <h2 id={`${id}-title`}>
            Готовим
            <br />
            <span>после заказа.</span>
          </h2>
        </div>
        <p>
          День сушистов начинается с заготовок и маркировки. А приготовление роллов — только после
          вашего заказа.
        </p>
      </div>

      <div className="ab-kitchen-scene">
        <div className="ab-kitchen-media">
          {STEPS.map((step, index) => (
            <div
              className="ab-kitchen-photo"
              role="tabpanel"
              key={step.photo}
              id={`${id}-panel-${index}`}
              aria-labelledby={`${id}-tab-${index}`}
              hidden={active !== index}
              tabIndex={0}
            >
              <Image
                src={step.photo}
                alt={step.alt}
                fill
                sizes="(max-width: 760px) calc(100vw - 32px), (max-width: 1344px) calc(100vw - 64px), 1280px"
                quality={85}
              />
            </div>
          ))}
        </div>

        <aside className="ab-kitchen-note" aria-label="Заметка с кухни">
          <div className="ab-kitchen-note-top">
            <span>Ручная работа</span>
            <span>Азиаточка</span>
          </div>
          <ol className="ab-kitchen-note-steps">
            <li>Подготовить ингредиенты</li>
            <li>Скрутить ролл</li>
            <li>Нарезать и упаковать</li>
          </ol>
          <div className="ab-kitchen-note-bottom">
            <span>От ингредиентов до ролла</span>
            <span aria-hidden="true">✓</span>
          </div>
        </aside>

        <div className="ab-kitchen-switch" role="tablist" aria-label="Процесс приготовления роллов">
          {STEPS.map((step, index) => (
            <button
              type="button"
              role="tab"
              key={step.label}
              id={`${id}-tab-${index}`}
              aria-controls={`${id}-panel-${index}`}
              aria-selected={active === index}
              tabIndex={active === index ? 0 : -1}
              onClick={() => setActive(index)}
              onKeyDown={(event) => handleTabKey(event, index)}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
            >
              <span aria-hidden="true">0{index + 1}</span>
              {step.label}
            </button>
          ))}
        </div>
      </div>
      <p className="ab-kitchen-caption" aria-live="polite" aria-atomic="true">
        {STEPS[active].description}
      </p>
    </section>
  );
}
